export interface Service {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  price: string;
  priceNote: string;
  timeline: string;
  /** Three short outcome-focused lines shown on the homepage service preview card. */
  bulletOutcomes: string[];
  includes: string[];
  bestFor: string;
  accentColor: string;
  popular?: boolean;
}

export const SERVICES: Service[] = [
  {
    slug: 'full-stack-saas',
    title: 'Full-Stack SaaS Development',
    subtitle: 'End-to-end web applications',
    description:
      'Complete SaaS platform from database schema to deployed product. Authentication, payments, dashboards, admin panels: the full stack. Designed for revenue from day one.',
    price: 'From $5,000',
    priceNote: 'Typical range: $5K–$15K depending on complexity',
    timeline: '2–6 weeks',
    bulletOutcomes: [
      'Auth, payments, dashboard, multi-tenant, wired up day one',
      'Production-ready Vercel deploy with CI/CD and previews',
      'You ship in 2–6 weeks; you own the code, the repo, the database',
    ],
    includes: [
      'Product strategy & architecture planning',
      'Database schema design (Prisma + Neon)',
      'Full frontend with responsive design',
      'Authentication & user management',
      'Payment integration (Stripe)',
      'Admin dashboard',
      'Vercel deployment with CI/CD',
      '30 days post-launch support',
    ],
    bestFor:
      'Founders and businesses who need a production-ready SaaS platform built fast.',
    accentColor: 'var(--accent)',
    popular: true,
  },
  {
    slug: 'ai-augmented-dev',
    title: 'AI-Augmented Development',
    subtitle: 'Team-scale output, verification-gated',
    description:
      "I leverage Claude Code and structured AI workflows to build at speeds traditional development can't match. Master prompts, multi-agent orchestration, and AI-powered testing accelerate every phase.",
    price: 'From $3,000',
    priceNote: 'Typical range: $3K–$10K',
    timeline: '1–4 weeks',
    bulletOutcomes: [
      'The output of a small team, with verification gates that keep AI-generated code honest, not vibes.',
      'Working MVP in days, not sprints, with Claude Code in the loop',
      'Master prompt + AI test coverage handed off for your team',
    ],
    includes: [
      'AI-optimized architecture design',
      'Claude Code pair programming',
      'Master prompt specification document',
      'Rapid prototyping (working MVP in days)',
      'AI-powered code review & testing',
      'Full deployment pipeline',
      'Documentation & handoff',
    ],
    bestFor:
      'Teams who want to see what AI-augmented development can actually deliver.',
    accentColor: 'var(--indigo)',
  },
  {
    slug: 'mvp-prototype',
    title: 'MVP / Rapid Prototype',
    subtitle: 'Idea to working product',
    description:
      'Go from concept to a working product with real users as fast as possible. Database, auth, core features, deployment: everything you need to start validating with real customers.',
    price: 'From $2,500',
    priceNote: 'Fixed scope, fixed price',
    timeline: '1–2 weeks',
    bulletOutcomes: [
      'Working product in 1–2 weeks, deployed to your own Vercel',
      'Auth, database, core feature, validate with real users on day 14',
      'Fixed scope, fixed price, source yours on delivery',
    ],
    includes: [
      'Requirements workshop (async or call)',
      'Database schema + API design',
      'Core feature implementation',
      'Basic authentication',
      'Responsive frontend',
      'Vercel deployment',
      'Source code handoff',
    ],
    bestFor:
      'Founders who need to validate an idea before investing in a full build.',
    accentColor: 'var(--orange)',
  },
  {
    slug: 'consulting',
    title: 'Technical Consulting',
    subtitle: 'Architecture, security & strategy',
    description:
      'Architecture reviews, security audits, AI integration strategy, performance optimization, and technical product roadmapping. Bring your toughest technical questions.',
    price: '$80/hr',
    priceNote: 'Minimum engagement: 2 hours',
    timeline: 'Flexible',
    bulletOutcomes: [
      'Architecture, security, or AI-integration review in hours, not weeks',
      'Written report with prioritized, actionable items',
      '$80/hr · 2-hour minimum · async-friendly',
    ],
    includes: [
      'Architecture review & recommendations',
      'Security audit with remediation plan',
      'AI integration strategy',
      'Performance optimization analysis',
      'Technical product roadmapping',
      'Written report with actionable items',
    ],
    bestFor:
      'Teams who have a product but need expert eyes on their architecture, security, or AI strategy.',
    accentColor: 'var(--rose)',
  },
];

export function getServiceBySlug(slug: string): Service | undefined {
  return SERVICES.find((s) => s.slug === slug);
}

export const PROJECT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'full-stack-saas', label: 'Full-Stack SaaS' },
  { value: 'ai-augmented-dev', label: 'AI-Augmented Dev' },
  { value: 'mvp-prototype', label: 'MVP / Prototype' },
  { value: 'consulting', label: 'Technical Consulting' },
  { value: 'other', label: 'Other' },
];

export const BUDGET_OPTIONS = [
  'Under $2,500',
  '$2,500–$5,000',
  '$5,000–$10,000',
  '$10,000–$25,000',
  '$25,000+',
  'Not sure yet',
];

export const TIMELINE_OPTIONS = [
  'ASAP',
  '1-2 weeks',
  '1 month',
  '2-3 months',
  'Flexible',
];

export const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'How fast can you build an MVP?',
    a: 'Most MVPs are deployed within 1–2 weeks. A full SaaS platform typically takes 2–6 weeks depending on complexity. I build fast because AI handles the repetitive work.',
  },
  {
    q: 'Do I own the code, IP, and accounts?',
    a: 'Yes, 100%. Full source code, full IP assignment on delivery, your own GitHub repo, your own Vercel project, your own database, your own domain. Nothing is licensed back to me. If you ever stop working with me, you keep everything and lose nothing.',
  },
  {
    q: 'What does payment look like?',
    a: '50% to start, 50% on delivery for fixed-scope projects. Larger engagements can split into 3-4 milestones tied to demoable progress. I send a Stripe invoice, no contracts in PDF purgatory. Hourly retainers are billed weekly.',
  },
  {
    q: 'What if scope changes mid-project?',
    a: "We talk. Small additions inside the original spirit of the project are usually absorbed. Material scope expansion gets a written change order with a delta on price and timeline before any work starts, so no surprise invoices and no scope creep on your dollar.",
  },
  {
    q: 'Do you sign NDAs?',
    a: "Yes, happily. I'll sign yours, or send a one-page mutual NDA before our first call. Default-confidential on everything you share unless you tell me otherwise.",
  },
  {
    q: 'What if I need changes after launch?',
    a: 'Every project includes 30 days of post-launch support: bug fixes, minor tweaks, deployment hand-off. After that, ongoing work is either a monthly retainer or per-feature. You are never trapped.',
  },
  {
    q: 'What if I am not happy with the work?',
    a: "If something is broken, I fix it on my time. That's not a refund question, it's a quality question. If we are mid-project and the relationship isn't working, we stop, I bill only for completed milestones, and you keep everything that's been delivered. No work product is held hostage.",
  },
  {
    q: 'Do you work with teams or only solo?',
    a: "I work solo but I can integrate with your existing team. I'm used to async communication, PR reviews, and collaborative workflows.",
  },
  {
    q: "What's your tech stack?",
    a: 'Next.js 14, React, TypeScript, Prisma ORM, Neon PostgreSQL, Tailwind CSS, Vercel. I can adapt if your project requires different technologies.',
  },
];
